"use client";

import { ctaClick } from "@/customAnalytics/trackingEvents";
import { LEAD_DROP_VARIANTS } from "@/constants/leadConstants";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { ProgramCardMetadata } from "@/types/programCard";
import Image from "next/image";
import React from "react";
import { useDispatch } from "react-redux";
import { Button } from "../atoms/Button";

export interface MobileCourseCardProps {
  data: ProgramCardMetadata;
  card_background_color: string;
  onBrochureDownload?: () => void;
  onKnowMore?: () => void;
  onCardClick?: () => void;
  className?: string;
}

const MobileCourseCard: React.FC<MobileCourseCardProps> = ({
  data,
  card_background_color,
  onKnowMore,
  onCardClick,
  className,
}) => {
  const dispatch = useDispatch();

  // Match CourseCard: open lead drop (DOWNLOAD) so phone / existing-lead / OTP run before brochure.
  // Do not use onBrochureDownload here — ProgramCards1 wired it to direct handleDownload, which skipped verification on mobile only.
  const handleBrochureClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    ctaClick({
      section: "explore_courses_section",
      component: "program_card",
      label:
        data?.meta_short_course_name ||
        data?.meta_full_course_name ||
        data?.id ||
        "brochure",
      step: "",
      entity: "brochure_download",
      landing_url: data?.meta_brochure_url || "",
    });
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.DOWNLOAD,
        downloadLink: data?.meta_brochure_url,
      }),
    );
  };

  const handleKnowMoreClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onKnowMore?.();
  };

  return (
    <div
      className={cn(
        "border border-gray-2 rounded-lg p-3 shadow-sm px-spacing-12 py-spacing-16 w-[332px]",
        onCardClick && "cursor-pointer hover:shadow-lg",
        className
      )}
      style={{ backgroundColor: card_background_color }}
      key={data?.id}
      onClick={onCardClick}
    >
      <div className="flex flex-row gap-6 justify-center items-center mb-spacing-28 ">
        <div className="relative flex-shrink-0 w-[74px] h-[64px] rounded-lg overflow-hidden">
          {data?.meta_thumbnail_image && (
            <Image
              src={data?.meta_thumbnail_image}
              alt={
                data?.meta_thumbnail_image_alt ||
                data?.meta_full_course_name ||
                "course"
              }
              fill
              className="object-cover"
            />
          )}
        </div>
        <div className="flex flex-col  ">
          <span
            className="text-[12px] font-normal leading-normal line-clamp-1 sm:text-[14px]"
            style={{ color: data?.meta_subtext_color }}
          >
            {data?.meta_subtext}
          </span>
          <p
            className="text-[18px] font-bold leading-normal line-clamp-1 sm:text-[18px] sm:font-semibold"
            style={{ color: data?.meta_course_name_text_color }}
          >
            {data?.meta_full_course_name}
          </p>
          <p
            className="text-[14px] font-normal leading-normal h-[42px] line-clamp-2 sm:text-[14px]"
            style={{ color: data?.meta_short_course_name_text_color }}
          >
            {data?.meta_short_course_name}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6 ">
        <div className="flex items-center gap-1 ">
          {data?.meta_enroll_icon && (
            <div className="relative w-[24px] h-[24px] flex-shrink-0">
              <Image
                src={data?.meta_enroll_icon}
                alt={data?.meta_enroll_icon_alt || "enroll icon"}
                fill
                className="object-contain"
              />
            </div>
          )}
          <span
            className="text-[12px] font-semibold leading-normal line-clamp-1 sm:text-[14px]"
            style={{ color: data?.meta_enroll_text_color }}
          >
            {data?.meta_enroll_text}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {data?.meta_money_icon && (
            <div className="relative w-[24px] h-[24px] flex-shrink-0 ">
              <Image
                src={data?.meta_money_icon}
                alt={data?.meta_money_icon_alt || "money icon"}
                fill
                className="object-contain"
              />
            </div>
          )}
          <span
            className="text-[12px] font-semibold leading-normal line-clamp-1 sm:text-[14px]"
            style={{ color: data?.meta_money_text_color || "#848484" }}
          >
            {data?.meta_money_text || data?.meta_subtext}
          </span>
        </div>
      </div>

      <div className="w-full h-0.5 bg-gray-2 rounded-full my-4" />

      <div className="flex justify-between gap-3">
        {data?.meta_enable_brochure && data?.meta_brochure_url && (
          <Button
            classType={"outline"}
            onClick={handleBrochureClick}
            withDownload={!!data?.meta_brochure_icon}
            downloadIcon={
              data?.meta_brochure_icon && (
                <div className="relative w-[18px] h-[18px]">
                  <Image
                    src={data?.meta_brochure_icon}
                    alt="brochure"
                    fill
                    className="object-contain"
                  />
                </div>
              )
            }
            className={cn(
              "flex-1 flex items-center justify-start gap-1 font-semibold leading-normal hover:opacity-80 transition-colors",
              "text-[14px] sm:text-[16px]",
              "rounded-lg"
            )}
            style={{
              borderColor:
                data?.meta_brochure_button_background_color ||
                data?.meta_brochure_text_color,
            }}
          >
            <span style={{ color: data?.meta_brochure_text_color }}>
              {data?.meta_brochure_text}
            </span>
          </Button>
        )}

        <Button
          classType={(data?.meta_cta_button_class_type as any) || "ghost"}
          onClick={handleKnowMoreClick}
          withArrow={!!data?.meta_cta_mobile_icon}
          arrowIcon={
            data?.meta_cta_mobile_icon && (
              <div className="relative w-[18px] h-[18px]">
                <Image
                  src={data?.meta_cta_mobile_icon}
                  alt={data?.meta_cta_mobile_icon_alt || "cta button icon"}
                  width={18}
                  height={18}
                />
              </div>
            )
          }
          className={cn(
            "flex-1 flex gap-3.5 font-semibold leading-normal transition-colors hover:opacity-80 xs:rounded-sm xs:px-2 xs:py-1",
            "text-[14px] sm:text-[16px]",
            data?.meta_cta_mobile_icon
              ? "items-center justify-end"
              : "items-center justify-center"
          )}
          style={{
            backgroundColor: data?.meta_cta_mobile_background_color,
          }}
        >
          <span style={{ color: data?.meta_cta_mobile_text_color }}>
            {data?.meta_cta_button_text}
          </span>
        </Button>
      </div>
    </div>
  );
};

export default MobileCourseCard;
